import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentCompany } from '@/app/providers/company-provider';
import { useDialog } from '@/app/providers/modal-provider/modal-provider.tsx';
import { Button } from '@/components/animate-ui/components/buttons/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { getProductBillingBlockUserMessage } from '@/entities/billing';
import { useCreateCompanyMutation } from '@/entities/company';
import { routes } from '@/shared/lib';
import { HStack, VStack } from '@/shared/ui';

export const CreateCompanyForm = memo(() => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const { mutateAsync } = useCreateCompanyMutation();
  const { closeDialog } = useDialog();
  const { setCurrentCompanyId } = useCurrentCompany();

  const handleCreateClick = async () => {
    try {
      const created = await mutateAsync({ name });
      setName('');
      closeDialog();
      await setCurrentCompanyId(created.id);
      navigate(routes.MAIN, { replace: true });
    } catch (err) {
      import('react-toastify').then(({ toast }) => {
        const billingMsg = getProductBillingBlockUserMessage(err);
        toast.error(billingMsg ?? 'Не удалось создать компанию. Проверьте введённые данные');
      });
    }
  };

  return (
    <VStack max align="start" gap="8" className="p-2">
      <div className="mb-2">
        <h2 className="text-2xl font-semibold tracking-tight">Создание компании</h2>
        <p className="text-xs text-muted-foreground/90 mt-1">
          Создайте новую компанию для организации групп встреч и воркспейсов
        </p>
      </div>

      <div className="w-full space-y-1">
        <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground/80 ml-1">
          Название компании
        </label>
        <Input
          className="w-full !bg-transparent border-0 border-b border-border/40 rounded-none px-1 h-10 focus-visible:ring-0 focus-visible:border-primary transition-all duration-200 md:text-base"
          placeholder="Например: Моя компания"
          onChange={(e) => setName(e.target.value)}
          value={name}
        />
      </div>

      <HStack max justify="end" gap="4" className="pt-4 w-full">
        <Button
          variant="ghost"
          className="cursor-pointer hover:bg-muted/50 active:scale-95 transition-all"
          onClick={closeDialog}
        >
          Отмена
        </Button>
        <Button
          disabled={!name.trim()}
          variant="default"
          className="cursor-pointer active:scale-95 transition-all shadow-md shadow-primary/20 px-8"
          onClick={handleCreateClick}
        >
          Создать
        </Button>
      </HStack>
    </VStack>
  );
});
